import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Leaf, Lock, Loader2, CheckCircle, AlertCircle, Shield, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      loadInviteInfo();
    } else {
      setError('No invitation token provided');
      setLoading(false);
    }
  }, [token]);

  const loadInviteInfo = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/invite/${token}`);
      setInviteInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/accept-invite`, {
        token,
        password
      });
      
      // Store token and redirect
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      
      setSuccess(true);
      toast.success('Account created successfully!');
      
      // Redirect after a moment
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Invalid Invitation</h2>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Welcome to Great Leaf Lending!</h2>
            <p className="text-slate-400 mb-6">Your account has been created. Redirecting to dashboard...</p>
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6" data-testid="accept-invite-page">
      <Toaster position="top-center" richColors />
      
      <Card className="max-w-md w-full bg-slate-800/50 border-slate-700 backdrop-blur-sm">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mb-4">
            <Leaf className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">Join Great Leaf Lending</CardTitle>
          <CardDescription className="text-slate-400">
            Complete your account setup
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Invite Info */}
          <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Email</span>
              <span className="text-white font-medium">{inviteInfo?.email}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Name</span>
              <span className="text-white font-medium">{inviteInfo?.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Access Level</span>
              <Badge className={inviteInfo?.role === 'admin' ? 'bg-emerald-600' : 'bg-blue-600'}>
                {inviteInfo?.role === 'admin' ? (
                  <><Shield className="h-3 w-3 mr-1" /> Full Admin</>
                ) : (
                  <><Eye className="h-3 w-3 mr-1" /> View Only</>
                )}
              </Badge>
            </div>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Create Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 pl-10"
                  required
                  minLength={6}
                  data-testid="invite-password"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 pl-10"
                  required
                  data-testid="invite-confirm-password"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={submitting}
              data-testid="invite-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
          
          <p className="text-xs text-slate-500 text-center">
            By creating an account, you agree to the terms of service.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitePage;
